import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  MenuItem,
  Checkbox,
  Radio,
  IconButton,
  Box,
  Typography,
  FormControl,
  FormHelperText,
  TextField,
  Tooltip,
  Popper,
  InputAdornment, Portal
} from "@mui/material";
import { ClickAwayListener } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  ChevronRight,
  Close as CloseIcon,
  RestartAlt as ResetIcon
} from "@mui/icons-material";
import InfoTooltip from "./InfoTooltip";
import { isEqual } from 'lodash'; // Import lodash's isEqual for deep comparison
import { useViewMode } from "../contexts/ViewModeContext";

/**
 * MultipleChoice Component
 *
 * Features:
 * - Single or Multi-Select (via `singleSelect`).
 * - Nested options (child submenus).
 * - "Revert" and "Close" buttons appear in the main menu only.
 * - Bolds any item that is selected or has a descendant that is selected.
 * - If `mandatory`, enforces at least one selection.
 * - Accepts an optional `instruction` string for a popover tooltip.
 * - Accepts a `questionText` string to display above the select button.
 * - Search: 
 *    - If a node matches, highlights and keeps its children
 *    - If a node does not match, keeps only matching children
 *    - Children of matched nodes that match are also highlighted
 *    - When only one node matches, auto expands to it 
 */
const MultipleChoice = ({
  questionText,
  options,
  allowAnyLevelSelection,
  onSelect,
  singleSelect,
  mandatory = false,
  showError = false,
  instruction,
  initialValue = [],
  customTrigger = null
}) => {
  // Main menu visibility
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Submenu anchor elements, keyed by path
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState({});

  // Which submenus are open, keyed by path
  const [openSubmenus, setOpenSubmenus] = useState({});

  // Add ref to track previous initialValue for comparison
  const prevInitialValueRef = useRef(initialValue);
  
  // Holds the selected item labels
  const [selectedItems, setSelectedItems] = useState(initialValue);

  // Check if view only mode
  const {isViewMode} = useViewMode();

  // Only update state when initialValue actually changes (deep comparison)
  useEffect(() => {
    // Skip update if values are deeply equal to prevent infinite loop
    if (!isEqual(prevInitialValueRef.current, initialValue)) {
      setSelectedItems(initialValue);
      prevInitialValueRef.current = initialValue;
    }
  }, [initialValue]);

  // Tracks which menu/submenu is the topmost (most recently opened)
  const [latestOpenedMenu, setLatestOpenedMenu] = useState("main");

  // If `mandatory` is true and no items selected, show an error
  const [error, setError] = useState(false);

  // For the optional instructions popover
  const [instructionAnchorEl, setInstructionAnchorEl] = useState(null);

  // Search term for filtering + highlighting
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    // whenever the searchTerm changes, close all submenus
    setOpenSubmenus({});
    setSubmenuAnchorEl({});
  }, [searchTerm]);

  // Ref for the answer box to position the menu
  const answerBoxRef = useRef(null);

  // Validate mandatory field on mount and when selectedItems changes
  useEffect(() => {
    if (mandatory && (!selectedItems || selectedItems.length === 0)) {
      setError(true);
    } else {
      setError(false);
    }
  }, [selectedItems, mandatory]);

  const menuRef = useRef(null);

  // Close dropdown when menu goes out of viewport
  useEffect(() => {
    if (!isMenuOpen || !menuRef.current) return;
  
    let animationFrameId;
  
    const isOutOfView = (rect) => {
      return (
        rect.bottom < 0 ||
        rect.top > window.innerHeight ||
        rect.right < 0 ||
        rect.left > window.innerWidth
      );
    };
  
    const checkAfterRender = () => {
      const rect = menuRef.current.getBoundingClientRect();
      if (isOutOfView(rect)) {
        handleCloseMenu();
      }
    };
  
    // Delay until the next paint cycle
    animationFrameId = requestAnimationFrame(checkAfterRender);
  
    const handleScrollOrResize = () => {
      const rect = menuRef.current.getBoundingClientRect();
      if (isOutOfView(rect)) {
        handleCloseMenu();
      }
    };
  
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
  
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isMenuOpen]);

  const menuDropdownRef = useRef(null);

  useEffect(() => {
    if (isMenuOpen && menuDropdownRef?.current) {
      menuDropdownRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isMenuOpen]);

  /*****************************************************
   * RECURSIVE HELPER: Check if item or its subtree is selected
   *****************************************************/
  const isNodeSelectedInSubtree = (item) => {
    if (selectedItems.includes(item.label)) return true;
    if (item.children) {
      return item.children.some((child) => isNodeSelectedInSubtree(child));
    }
    return false;
  };

  /*****************************************************
   * HIGHLIGHT PORTIONS OF LABEL THAT MATCH THE SEARCH TERM
   *****************************************************/
  const highlightText = (text, term) => {
    if (!term) return text;
    const regex = new RegExp(`(${term})`, "i");
    const matchIndex = text.search(regex);
    if (matchIndex === -1) return text;

    const matchStr = text.substring(matchIndex, matchIndex + term.length);
    return (
      <>
        <strong>
          {text.substring(0, matchIndex)}
          <mark>{matchStr}</mark>
          {text.substring(matchIndex + term.length)}
        </strong>
      </>
    );
  };

  /*****************************************************
   * FILTER & HIGHLIGHT:
   * If parent matches, highlight it, keep all children but highlight children that also match
   * If parent doesn't match, only keep children that match.
   *****************************************************/
  const highlightAllDescendants = (items, term) => {
    if (!items) return [];
    return items.map((child) => {
      const childMatches = child.label.toLowerCase().includes(term.toLowerCase());
      const newLabel = childMatches
        ? highlightText(child.label, term)
        : child.label;

      return {
        ...child,
        displayLabel: newLabel,
        children: child.children
          ? highlightAllDescendants(child.children, term)
          : undefined
      };
    });
  };

  const filterAndHighlight = (items, term) => {
    if (!term.trim()) {
      // If no search term, just return items with displayLabel = label
      return items.map((item) => ({
        ...item,
        displayLabel: item.label,
        children: item.children
          ? filterAndHighlight(item.children, term)
          : undefined
      }));
    }

    const result = [];
    for (const item of items) {
      const labelMatches = item.label.toLowerCase().includes(term.toLowerCase());

      if (labelMatches) {
        // Highlight this item, keep all children
        result.push({
          ...item,
          displayLabel: highlightText(item.label, term),
          children: item.children
            ? highlightAllDescendants(item.children, term)
            : undefined
        });
      } else if (item.children) {
        // Parent doesn't match => see if any children match
        const filteredChildren = filterAndHighlight(item.children, term);
        if (filteredChildren.length > 0) {
          // Keep parent (unhighlighted) if children exist
          result.push({
            ...item,
            displayLabel: item.label,
            children: filteredChildren
          });
        }
      }
      // else discard
    }
    return result;
  };

  // Filtered + highlighted options
  const filteredOptions = useMemo(
    () => filterAndHighlight(options, searchTerm),
    [options, searchTerm]
  );

  /*****************************************************
   * FIND ALL MATCHING NODES
   *****************************************************/
  const getAllMatchingNodes = (items, term) => {
    let matches = [];
    for (const item of items) {
      const labelMatches = item.label.toLowerCase().includes(term.toLowerCase());
      // If the current node's label matches, add it
      if (labelMatches) {
        matches.push(item);
      }
      // Recurse into children (if any) regardless of whether parent matched
      if (item.children && item.children.length > 0) {
        matches = matches.concat(getAllMatchingNodes(item.children, term));
      }
    }
    return matches;
  };

  const allMatches = useMemo(
    () => searchTerm.trim()
      ? getAllMatchingNodes(options, searchTerm)
      : [],
    [options, searchTerm]
  );

  /*****************************************************
   * FIND THE PATH FROM THE TOP LEVEL DOWN TO A TARGET NODE
   *****************************************************/
  const findNodePath = (items, targetNode, pathSoFar = []) => {
    for (let item of items) {
      const newPath = [...pathSoFar, item];
      // Compare by reference (since `item` is from filteredOptions)
      if (item === targetNode) {
        return newPath;
      }
      if (item.children) {
        const childPath = findNodePath(item.children, targetNode, newPath);
        if (childPath) {
          return childPath;
        }
      }
    }
    return null;
  };

  // 1) Helper to drop any match whose ancestor is also matched
  const pruneMatches = (matches) => {
    const labels = new Set(matches.map((m) => m.label));
    return matches.filter((item) => {
      const path = findNodePath(options, item) || [];
      // path is [root, …, item]; drop if any ancestor (path[i], i < last) is matched
      for (let i = 0; i < path.length - 1; i++) {
        if (labels.has(path[i].label)) {
          return false;
        }
      }
      return true;
    });
  };

  const prunedMatches = useMemo(() => pruneMatches(allMatches), [allMatches]);

  const findItemByLabel = (items, label) => {
    for (const item of items) {
      if (item.label === label) return item;
      if (item.children) {
        const found = findItemByLabel(item.children, label);
        if (found) return found;
      }
    }
    return null;
  };
  
  const getFullPathLabel = (label) => {
    const item = findItemByLabel(options, label);
    if (!item) return label;
    const path = findNodePath(options, item);
    if (!path) return label;
    return path.map(node => node.label).join(" → ");
  };

  const getLastLevelLabel = (label) => {
    // For multi-answer display outside the menu, show only the last-level text
    return label;
  };

  /*****************************************************
   * MENU OPEN/CLOSE HANDLERS
   *****************************************************/
  const handleToggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    setSubmenuAnchorEl({});
    setOpenSubmenus({});
    setLatestOpenedMenu("main");
    setSearchTerm("");
  };

  const handleCloseMenu = () => {
    setIsMenuOpen(false);
    setSubmenuAnchorEl({});
    setOpenSubmenus({});
    setLatestOpenedMenu("main");
    setSearchTerm("");
    console.trace("WTF??????")
  };

  const handleSubmenuOpen = (event, key) => {
    const parentKey = key.substring(0, key.lastIndexOf("-"));
    setSubmenuAnchorEl((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith(parentKey + "-")) {
          updated[k] = prev[k];
        }
      });
      return { ...updated, [key]: event.currentTarget };
    });
    // Close all submenus that have the same parent key
    setOpenSubmenus((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith(parentKey + "-")) {
          updated[k] = prev[k];
        }
      });
      return { ...updated, [key]: true };
    });
    setLatestOpenedMenu(key);
  };

  const handleSubmenuClose = (key) => {
    const parentKey = key.substring(0, key.lastIndexOf("-"));
    const hasChildSubmenus = Object.keys(openSubmenus).some((k) => {
      return k.startsWith(`${parentKey}-`);
    });
    if (!hasChildSubmenus) return;

    setSubmenuAnchorEl((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith(parentKey + "-")) {
          updated[k] = prev[k];
        }
      });
      return { ...updated };
    });
    // Close all submenus that have the same parent key
    setOpenSubmenus((prev) => {
      const updated = {};
      Object.keys(prev).forEach((k) => {
        if (!k.startsWith(parentKey + "-")) {
          updated[k] = prev[k];
        }
      });
      // const stillOpen = Object.keys(openSubmenus).filter((k) => k !== key);
      // setLatestOpenedMenu(stillOpen.length ? stillOpen[stillOpen.length - 1] : "main");
      return { ...updated };
    });
  };

  /*****************************************************
   * REVERT & SELECTION LOGIC
   *****************************************************/
  const handleClearSelections = () => {
    setSelectedItems([]);
    onSelect?.([]);
    setError(mandatory);
  };

  const handleSelection = (event, item) => {
    event.stopPropagation();
    if (!allowAnyLevelSelection && item.children) return;

    let newSelectedItems;

    if (singleSelect) {
      newSelectedItems = [item.label];
      setSelectedItems(newSelectedItems);
      onSelect?.(newSelectedItems);
      handleCloseMenu();
    } else {
      if (selectedItems.includes(item.label)) {
        newSelectedItems = selectedItems.filter((lbl) => lbl !== item.label);
      } else {
        newSelectedItems = [...selectedItems, item.label];
      }
      if (item.children) {
        item.children.forEach((child) => {
          newSelectedItems = newSelectedItems.filter((lbl) => lbl !== child.label);
        });
      }
      setSelectedItems(newSelectedItems);
      onSelect?.(newSelectedItems);
    }

    if (mandatory && (!newSelectedItems || newSelectedItems.length === 0)) {
      setError(true);
    } else {
      setError(false);
    }
  };

  const handleRemoveSelection = (labelToRemove) => {
    const newSelectedItems = selectedItems.filter(label => label !== labelToRemove);
    setSelectedItems(newSelectedItems);
    onSelect?.(newSelectedItems);

    if (mandatory && newSelectedItems.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
  };

  /*****************************************************
   * RENDER MENU ITEMS (RECURSIVE)
   *****************************************************/
  const isCurrentPage = (menuKey) => latestOpenedMenu === menuKey;

  const renderSelectionControl = (isSelected, item) => {
    if (singleSelect) {
      return (
        <Radio
          checked={isSelected}
          onClick={(e) => handleSelection(e, item)}
          tabIndex={-1}
        />
      );
    } else {
      return (
        <Checkbox
          checked={isSelected}
          onClick={(e) => handleSelection(e, item)}
          tabIndex={-1}
        />
      );
    }
  };

  const renderMenuItems = (items, parentKey = "main") => {
    return items.map((item) => {
      const key = `${parentKey}-${item.label}`;
      const isSelected = selectedItems.includes(item.label);
      const hasChildren = item.children && item.children.length > 0;

      // If multi-select & an item has children & is selected,
      // we won't show its children in the same menu
      if (!singleSelect && hasChildren && isSelected) {
        return (
          <MenuItem key={key} onClick={(e) => handleSelection(e, item)} sx={{ '&:hover': { backgroundColor: 'lightblue' } }}>
            <Checkbox checked={isSelected} sx={{ marginRight: 1 }} tabIndex={-1} />
            {item.displayLabel ?? item.label}
          </MenuItem>
        );
      }

      // If it has children, create a nested submenu
      if (hasChildren) {
        return (
          <div key={key} style={{ position: "relative" }}>
            <MenuItem
              onMouseEnter={(e) => handleSubmenuOpen(e, key)}
              sx={{ '&:hover': { backgroundColor: 'lightblue' } }}
            >
              {allowAnyLevelSelection && (
                <div style={{ marginRight: 8 }} onClick={(e) => e.stopPropagation()}>
                  {renderSelectionControl(isSelected, item)}
                </div>
              )}
              <span
                style={{
                  fontWeight: isNodeSelectedInSubtree(item) ? "bold" : "normal"
                }}
              >
                {item.displayLabel ?? item.label}
              </span>
              <ChevronRight sx={{ ml: "auto" }} />
            </MenuItem>

            <Popper
              strategy="fixed"
              open={Boolean(openSubmenus[key] && submenuAnchorEl[key])}
              anchorEl={submenuAnchorEl[key]}
              placement="right-start"
              modifiers={[{ name: "offset", options: { offset: [0, 8] }, },
              { name: "flip", options: { fallbackPlacements: ["left-start"] } },
              {
                name: "preventOverflow",
                options: {
                  boundary: 'viewport',
                  padding: 8,
                  altAxis: true,
                },
              },]}
              style={{ zIndex: 1500 }}
            >
              <Box
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  boxShadow: 3,
                  maxHeight: "60vh",
                  overflowY: "auto",
                  width: "auto",
                  p: 1,
                  // show scrollbar
                  scrollbarWidth: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ flexGrow: 1, textAlign: "center" }}>
                    {item.label}
                  </Typography>
                </Box>
                {renderMenuItems(item.children, key)}
              </Box>
            </Popper>
          </div>
        );
      }

      // Leaf item
      return (
        <MenuItem 
          key={key}
          onClick={(e) => { 
            handleSelection(e, item)
          }}
          onMouseOver={(e) => { 
            handleSubmenuClose(key)
          }}
          sx={{ '&:hover': { backgroundColor: 'lightblue' } }}
        >
          {renderSelectionControl(isSelected, item)}
          <span style={{ marginLeft: 8 }}>{item.displayLabel ?? item.label}</span>
        </MenuItem>
      );
    });
  };

  /*****************************************************
   * MAIN RENDER
   *****************************************************/
  return (
    <div className="annotation-form-field-container" style={{ pointerEvents: isViewMode ? "none" : "auto" }}>
      {questionText && !customTrigger && (
        <div className="annotation-form-field-label">
          <span className={`label-text ${mandatory ? 'mandatory' : ''}`}>
            {questionText}
          </span>
          {mandatory && <span className="required-asterisk">*</span>}
          {instruction && <InfoTooltip instruction={instruction}/>}
        </div>
      )}

      <FormControl error={showError && error} ref={menuRef} required={mandatory} fullWidth className="annotation-form-field-input">

      <ClickAwayListener onClickAway={() => isMenuOpen && handleCloseMenu()}>
        <Box sx={{ position: "relative" }}>
          {customTrigger ? (
            // custom multiple choice component
            React.cloneElement(customTrigger, { onClick: handleToggleMenu, ref: answerBoxRef })
          ) : (
            // default multiple choice component
            <Box
              ref={answerBoxRef}
              onClick={handleToggleMenu}
              sx={{
                display: "flex",
                alignItems: singleSelect ? "center" : "flex-start",
                justifyContent: singleSelect ? "space-between" : "flex-start",
                flexWrap: singleSelect ? "nowrap" : "wrap",
                width: "100%",
                minHeight: "40px",
                ...(singleSelect ? { height: "40px" } : { paddingTop: "8px", paddingBottom: "8px" }),
                bgcolor: "#fff",
                border: "1px solid #ccc",
                borderRadius: "4px",
                px: "14px",
                cursor: "pointer",
                fontSize: "14px",
                boxShadow: "none",
                "&:hover": { borderColor: "#888" },
                "&:focus": { borderColor: "#1976d2", outline: "none" },
                gap: singleSelect ? 0 : "4px",
              }}
            >
            {selectedItems.length === 0 ? "" : singleSelect ? (
              // Single-answer: Display in-place with distinct styling (green, bold, no enclosing shape)
              <Typography
                sx={{
                  color: "#2e7d32", // Green for single-answer
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                {getLastLevelLabel(selectedItems[0])}
              </Typography>
            ) : (
              // Multi-answer: Display in-place with distinct styling (blue, rounded box)
              selectedItems.map((label, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#e3f2fd", // Light blue background for multi-answer
                  borderRadius: "12px",
                  px: 1,
                  py: 0.5,
                  mr: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.85rem",
                    color: "#1976d2", // Blue for multi-answer
                    mr: 1,
                  }}
                >
                  {getLastLevelLabel(label)}
                </Typography>
                {!isViewMode && <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveSelection(label);
                  }}
                  title="Remove selection"
                  sx={{ p: 0, color: "red" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>}
              </Box>
              ))
            )}

            {!isViewMode && (
              <ChevronRight
                sx={{
                  transform: isMenuOpen ? "rotate(-90deg)" : "rotate(90deg)", // Rotate based on menu state
                  color: "#888",
                  fontSize: "1.2rem",
                  transition: "transform 0.3s ease",
                  ml: "auto",
                }}
              />
            )}
          </Box>
        )}

          {/* Dropdown Menu (Unrolls Below) */}
          {isMenuOpen && (
            <Portal>
              <Popper
                open={isMenuOpen}
                anchorEl={answerBoxRef.current}
                placement="bottom-start"
                sx={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  mt: 1,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  // maxHeight: "80vh",
                  overflowY: "scroll",
                  zIndex: 1300,
                  transition: "max-height 0.3s ease, opacity 0.3s ease",
                  opacity: isMenuOpen ? 1 : 0,
                  maxHeight: isMenuOpen ? "50vh" : 0,
                  minWidth: "300px",
                  // show scrollbar
                  scrollbarWidth: "auto",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background: "#f1f1f1",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background: "#888",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    background: "#555",
                  },
                }}
                ref={menuDropdownRef}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    p: 1,
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ textAlign: "center", fontWeight: 500 }}>
                      Main Menu
                    </Typography>
                    <Box>
                      <Tooltip title="Delete All / 删除所有">
                        <IconButton size="small" onClick={handleClearSelections}>
                          <ResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                    }}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment> }
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        borderRadius: "6px",
                        bgcolor: "#f5f5f5",
                      },
                    }}
                  />
                </Box>

                {/* Render Filtered Items (or exactly five matches when there are five) */}
                {(() => {
                  if (searchTerm.trim() && prunedMatches.length > 0 && prunedMatches.length <= 5) {
                    const flat = prunedMatches.map(item => ({
                      ...item,
                      displayLabel: highlightText(item.label, searchTerm),
                      children: item.children
                        ? highlightAllDescendants(item.children, searchTerm)
                        : undefined,
                    }));
                    return renderMenuItems(flat);
                  }
                  return renderMenuItems(filteredOptions);
                })()}
              </Popper>
            </Portal>
          )}
        </Box>
      </ClickAwayListener>

      {/* If mandatory & no selections, show "Selection is required" */}
      {mandatory && selectedItems.length === 0 && showError && (
        <FormHelperText>Selection is required.</FormHelperText>
      )}
      </FormControl>
    </div>
  );
};

export default MultipleChoice;
